import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { Popover } from '@mui/material';
import { produce } from 'immer';
import { CollapsableForm } from '@bbkAdminComponents/play/play-utils';
import OfferPresentationMode, {
  OfferPresentationModeDropdown,
} from '@bbkAdminComponents/play/offer-presentation-mode';
import {
  ADD_OFFER_VALUE,
  PlayContext,
  PlayObjectProperty,
  PlayValidationErrorType,
} from '@bbkAdminComponents/play/play-context';
import {
  type PercentNodeType,
  type PercentToTargetType,
} from '@bbkAdminUtils/api-client/routing.types';
import GenericSelect from '@bbkAdminComponents/select/select';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { offersAndLossAversionsSlice } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import { generateNewIdInt } from '@bbkAdminComponents/experiences/offers/utils';
import icons from '@bbkAdminUtils/icons';
import {
  appPersistSlice,
  selectAppMisc,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { cs } from '@bbkAdminUtils/css-utils';
import {
  RoutingNodeEnum,
  RoutingPercentEnum,
} from '@bbkAdminUtils/api-client/rest-routing-api';
import { PlayControlSelect } from '@bbkAdminComponents/play/play-control-select';
import { AddControlButton } from '@bbkAdminComponents/play/add-control-button';
import { DisableContainer } from '@bbkAdminComponents/play/disable-container';
import { GenericActionMenu } from '@bbkAdminComponents/action-menu/generic-action-menu';
import { ActionMenuIcons } from '@bbkAdminComponents/action-menu/action-menu-icons';
import { OfferNodeType } from '@bbkAdminComponents/play/play-offer-node-select';
import { ManageOfferWarning } from '@bbkAdminComponents/play/offer-components/single-offer';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { useEnhancedOfferSelect } from '@bbkAdminComponents/play/offer-components/enhanced-offer-select';
import { useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { FeatureFlagsE } from '@bbkAdminUtils/feature-flags';

enum SelectProperty {
  PERCENT = 'percent',
  OFFER = 'offer',
}

export type ABTestOfferErrorType = {
  type: OfferNodeType.TEST;
  offerErrors: Array<string | undefined>;
  percentError: string | undefined;
  hasError?: boolean;
  hasValidOffer: boolean;
  show: boolean;
  isInvalidControlPercentage?: boolean;
};

export const ABTestOffer = () => {
  const {
    play,
    getPlayObjectProperty,
    setPlayObjectProperty,
    resetOfferNode,
    handleCreateNewOffer,
    offerOptions,
    playValidationErrors,
    setPlayValidationErrors,
    getDropdownTitle,
    getActiveTarget,
    handleEditOffer,
    handleCrossObjectiveOfferSelect,
    allOffers,
    showManageOfferWarning,
  } = useContext(PlayContext);
  const offerPresentation = getPlayObjectProperty(
    PlayObjectProperty.PRESENTATION
  );
  const playType = getPlayObjectProperty(PlayObjectProperty.PLAY_TYPE);

  const activeTarget = getActiveTarget();
  const offerNode = getPlayObjectProperty(
    PlayObjectProperty.OFFER_NODE
  ) as PercentNodeType;
  const appKey = useSelector(selectAppId);
  const anchorRef = useRef(null);

  const dispatch = useAppDispatch();
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const isMultiBranded = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );

  const showAddControl =
    getPlayObjectProperty(PlayObjectProperty.CONTROL_PERCENTAGE) === 0 &&
    getPlayObjectProperty(PlayObjectProperty.CONTROL_TARGET) === 'none';

  const { isLoading: isOffersLoading, isFetching: isOfferFetching } =
    offersAndLossAversionsSlice.endpoints.getOffers.useQuery({ appKey });

  const offerData: {
    percent: number;
    target: { type: string; target_key?: string };
  }[] = useMemo(() => {
    if (!_.get(offerNode, 'routing_node.routing_percents')) {
      // init targeting node
      const initialNode = {
        ...offerNode,
        routing_node: {
          type: RoutingNodeEnum.percentRoutingNode,
          name: generateNewIdInt('pct_split'),
          routing_percents: [
            {
              type: RoutingPercentEnum.routingToTargetPercent,
              name: generateNewIdInt('pct_target'),
              percent: 50,
              target: {
                type: activeTarget,
              },
            },
            {
              type: RoutingPercentEnum.routingToTargetPercent,
              name: generateNewIdInt('pct_target'),
              percent: 50,
              target: {
                type: activeTarget,
              },
            },
          ],
        },
      };
      setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, initialNode);
      return [];
    }
    const routingPercents = _.get(
      offerNode,
      'routing_node.routing_percents',
      []
    ) as PercentToTargetType[];
    return routingPercents.map((node) => ({
      percent: node.percent,
      target: node.target,
    }));
  }, [offerNode, activeTarget]);

  const validationErrorPath = [PlayValidationErrorType.Offer];

  const ABTestOfferValidationError = useMemo(() => {
    const errorNode = _.get(playValidationErrors, validationErrorPath);
    if (errorNode?.type === OfferNodeType.TEST) {
      return errorNode;
    }
    return undefined;
  }, [playValidationErrors]);

  const showError = ABTestOfferValidationError?.show;
  const percentError = ABTestOfferValidationError?.percentError;

  useEffect(() => {
    const errors: (string | undefined)[] = [];
    let hasError = false;
    let percentSum = 0;
    let negPercent = false;
    let hasValidOffer = false;
    offerData.forEach((data, index) => {
      const offer_key = _.get(data, ['target', 'target_key']);
      if (_.isEmpty(offer_key)) {
        errors[index] = 'Select a valid offer';
        hasError = true;
      } else {
        errors[index] = undefined;
        hasValidOffer = true;
      }
      percentSum += data.percent;
      if (data.percent < 0) {
        negPercent = true;
      }
    });
    const percentErrorMessage = negPercent
      ? 'Value must be between 0 and 100.'
      : percentSum !== 100
        ? 'Sum of all offers should add up to 100%'
        : undefined;

    if (percentErrorMessage) hasError = true;

    setPlayValidationErrors((prev) => {
      const updatedValidationErrors = produce(prev, (draft) => {
        _.set(draft, validationErrorPath, {
          ..._.get(draft, validationErrorPath),
          type: OfferNodeType.TEST,
          offerErrors: errors,
          percentError: percentErrorMessage,
          hasError,
          hasValidOffer,
          show: false,
        });
      });
      return updatedValidationErrors;
    });
  }, [offerData]);

  useEffect(() => {
    if (!_.get(offerNode, 'routing_node.routing_percents', undefined)) return;
    const playState = appMiscState.playState;
    if (playState?.addNewOffer?.path === 'TO_AB_TEST') {
      const updatedNode = produce(offerNode, (draft) => {
        _.set(
          draft,
          ['routing_node', 'routing_percents', 0, 'target'],
          playState.addNewOffer?.value
        );
      });
      setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, updatedNode);
      dispatch(
        appPersistSlice.actions.saveAppMisc({
          appKey,
          playState: {
            play: playState?.play,
            addNewOffer: undefined,
          },
        })
      );
    }
  }, [offerNode]);

  const handlePropertyChange = (
    field: SelectProperty,
    value: string,
    index: number
  ) => {
    switch (field) {
      case SelectProperty.PERCENT: {
        const updatedNode = produce(offerNode, (draft) => {
          _.set(
            draft,
            ['routing_node', 'routing_percents', index, 'percent'],
            parseInt(value)
          );
        });
        setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, updatedNode);
        break;
      }
      case SelectProperty.OFFER: {
        const updatedNode = produce(offerNode, (draft) => {
          _.set(
            draft,
            ['routing_node', 'routing_percents', index, 'target', 'target_key'],
            value
          );
        });
        setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, updatedNode);
        break;
      }
    }
  };

  const distributeEvenly = (total: number, n: number) => {
    const quotient = Math.floor(total / n);
    const remainder = total % n;

    return Array(n)
      .fill(quotient)
      .map((value, index) => value + (index < remainder ? 1 : 0));
  };

  const handleDistributePercentEvenly = () => {
    const updatedNode = produce(offerNode, (draft) => {
      const routingPercents = _.get(draft, 'routing_node.routing_percents', []);
      const evenDistribute = distributeEvenly(100, routingPercents.length);
      evenDistribute.forEach((value, index) => {
        _.set(routingPercents, [index, 'percent'], value);
      });
      _.set(draft, ['routing_node', 'routing_percents'], routingPercents);
    });
    setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, updatedNode);
  };

  const enhancedOfferSelect = useEnhancedOfferSelect({
    offers: allOffers,
    playType: playType as any,
    offerPresentation,
    isMultiBranded,
    onOfferSelect: (offerId: string) => {},
    onCreateNewOffer: () => {
      setAddNewOfferPath(0);
      handleCreateNewOffer();
    },
    onCrossObjectiveOfferSelect: handleCrossObjectiveOfferSelect,
  });

  const appMiscState = useSelector(selectAppMisc(appKey));
  const handleAddRow = () => {
    if (offerData.length >= 5) {
      return;
    }

    const node = {
      type: RoutingPercentEnum.routingToTargetPercent,
      name: generateNewIdInt('pct_target'),
      percent: 0,
      target: {
        type: activeTarget,
      },
    };
    const updatedNode = produce(offerNode, (draft) => {
      const routingPercents = _.get(
        draft,
        'routing_node.routing_percents',
        []
      ) as PercentToTargetType[];
      routingPercents.push(node as PercentToTargetType);

      const evenDistribute = distributeEvenly(100, routingPercents.length);
      routingPercents.forEach((rp, idx) => {
        rp.percent = evenDistribute[idx];
      });

      _.set(draft, ['routing_node', 'routing_percents'], routingPercents);
    });
    setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, updatedNode);
  };

  const handleRemoveRow = (index: number) => {
    const updatedNode = produce(offerNode, (draft) => {
      const routingPercents = _.get(
        draft,
        'routing_node.routing_percents',
        []
      ) as PercentToTargetType[];
      routingPercents.splice(index, 1);

      const evenDistribute = distributeEvenly(100, routingPercents.length);
      routingPercents.forEach((rp, idx) => {
        rp.percent = evenDistribute[idx];
      });

      _.set(draft, ['routing_node', 'routing_percents'], routingPercents);
    });
    setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, updatedNode);
  };
  const setAddNewOfferPath = (index: number) => {
    dispatch(
      appPersistSlice.actions.saveAppMisc({
        appKey,
        playState: {
          play,
          addNewOffer: {
            path: [
              'draft_targeting',
              'routing_percents',
              1,
              'routing_node',
              'routing_percents',
              index,
              'target',
            ],
            value: undefined,
          },
        },
      })
    );
  };

  const title = (
    <div>
      <span className="tw-mr-2">Test</span>
      <button ref={anchorRef} onClick={() => setOpenDropdown(true)}>
        {icons.threeDots}
      </button>
      <Popover
        open={openDropdown}
        anchorEl={anchorRef.current}
        onClose={() => setOpenDropdown(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div>
          <button
            className="tw-block tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-cbrdanger-400 hover:tw-bg-[#E1F1F9]"
            onClick={resetOfferNode}
          >
            <span className="tw-mr-1">{icons.trash1}</span>
            Remove
          </button>
        </div>
      </Popover>
    </div>
  );
  const getActions = (offerId: string | undefined, index: number) => [
    {
      icon: ActionMenuIcons.pencil,
      copy: 'Edit',
      onClick: () => {
        handleEditOffer(offerId);
        setAddNewOfferPath(index);
      },
    },
    {
      icon: ActionMenuIcons.duplicate,
      copy: 'Copy',
      onClick: () => {
        handleEditOffer(offerId, true);
        setAddNewOfferPath(index);
      },
    },
  ];

  return (
    <div className="ab-test-offer tw-flex tw-border tw-border-transparent">
      <div className="tw-flex tw-flex-col tw-items-center">
        <div className="tw-mb-8 tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#DCB2FD] tw-bg-[#DABFF5]">
          {icons.percent}
        </div>
      </div>
      <div className="tw-py-2 tw-pl-5">
        <DisableContainer editableAfterLive>
          <CollapsableForm
            title={title}
            id={3}
            collapseElement={<div className="tw-min-w-[45rem]" />}
          >
            {offerPresentation === undefined ? (
              <OfferPresentationMode />
            ) : (
              <div className="tw-mb-5 tw-mt-4 tw-min-w-[45rem] tw-rounded-sm tw-border tw-border-[#DCB2FD] tw-bg-white tw-px-6 tw-py-3">
                <div className="tw-px-3 tw-py-2">
                  <div className="tw-flex tw-items-center">
                    <span className="tw-mr-2 tw-font-semibold">Widget:</span>
                    <OfferPresentationModeDropdown />
                    {showAddControl && <AddControlButton />}
                  </div>
                  {showAddControl && (
                    <div className="tw-my-5 tw-w-full tw-border-b tw-border-solid tw-border-[#DDDFE2] tw-px-10" />
                  )}
                  {!showAddControl && (
                    <PlayControlSelect
                      componentTitle="Variants"
                      multipleOfferPages
                    />
                  )}
                  <div className="tw-mb-4 tw-pr-10">
                    <div className="tw-flex tw-font-semibold">
                      <div className="tw-flex tw-basis-1/4" />
                      <div className="tw-basis-1/4">Split %</div>
                      <div className="tw-basis-3/4 tw-pl-5">
                        {getDropdownTitle()}
                        <span className="tw-w-5">
                          {(isOffersLoading || isOfferFetching) &&
                            icons.loaderSW6}
                        </span>
                      </div>
                    </div>
                    {offerData?.map((row, index) => {
                      const offerId = row.target?.target_key;
                      const manageOfferWarning =
                        showManageOfferWarning(offerId);
                      return (
                        <div
                          className="tw-mt-3 tw-flex tw-items-center"
                          key={index}
                        >
                          <div className="tw-basis-1/4 tw-font-semibold">
                            Variant {index + 1}
                          </div>
                          <div className="tw-flex tw-basis-1/4 tw-items-start tw-pr-6">
                            <div className="tw-flex tw-items-center">
                              <input
                                className={cs(
                                  'bbk-simple-text-input !tw-pr-6',
                                  {
                                    '!tw-border-cbrdanger-400': percentError,
                                  }
                                )}
                                type="number"
                                value={row.percent}
                                onChange={(e) =>
                                  handlePropertyChange(
                                    SelectProperty.PERCENT,
                                    e.currentTarget.value,
                                    index
                                  )
                                }
                              />
                              <span className="-tw-translate-x-5">%</span>
                            </div>
                          </div>
                          <div className="tw-basis-3/4 tw-pl-5">
                            <div className="tw-flex tw-flex-row tw-items-center">
                              <GenericSelect
                                className="tw-mr-2 tw-w-5/6"
                                options={
                                  enhancedOfferSelect?.groupedOptions ||
                                  offerOptions
                                }
                                value={
                                  offerOptions.find(
                                    (o) => o.value === row.target?.target_key
                                  ) ?? null
                                }
                                onChange={(e) => {
                                  if (enhancedOfferSelect?.onSelect) {
                                    const crossObjectiveOffer =
                                      enhancedOfferSelect.categorization?.nonMatchingOffers.find(
                                        (categorizedOffer: any) =>
                                          categorizedOffer.offer.id === e.value
                                      );
                                    if (crossObjectiveOffer) {
                                      setAddNewOfferPath(index);
                                      handleCrossObjectiveOfferSelect(
                                        crossObjectiveOffer.offer
                                      );
                                      return;
                                    }
                                    enhancedOfferSelect.onSelect(e.value);
                                  }

                                  if (e.value === ADD_OFFER_VALUE) {
                                    setAddNewOfferPath(index);
                                    handleCreateNewOffer();
                                    return;
                                  }
                                  handlePropertyChange(
                                    SelectProperty.OFFER,
                                    e.value,
                                    index
                                  );
                                }}
                              />
                              {offerOptions.find(
                                (o) => o.value === offerId
                              ) && (
                                <GenericActionMenu
                                  actions={getActions(offerId, index)}
                                />
                              )}
                              {manageOfferWarning && (
                                <TippyTooltip
                                  tippyOptions={{
                                    placement: 'right',
                                  }}
                                  content={<ManageOfferWarning />}
                                >
                                  <span className="tw-ml-4 tw-text-xs tw-text-[#FFB300] [&_svg]:tw-h-5 [&_svg]:tw-w-5">
                                    {icons.warning}
                                  </span>
                                </TippyTooltip>
                              )}
                            </div>
                          </div>
                          {showError &&
                            ABTestOfferValidationError?.offerErrors[index] && (
                              <span className="tw-pl-1 tw-text-xs tw-text-danger-500">
                                {ABTestOfferValidationError?.offerErrors[index]}
                              </span>
                            )}
                          <div className="tw-w-4 tw-pl-1 tw-pt-1">
                            {index > 1 && (
                              <button
                                className="trash-icon tw-ml-1"
                                onClick={() => handleRemoveRow(index)}
                              >
                                <span>{icons.trash1}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div>
                      {percentError && (
                        <span className="tw-mt-2 tw-text-sm tw-text-cbrdanger-400">
                          {percentError}
                        </span>
                      )}
                    </div>
                  </div>
                  {offerData.length < 5 && (
                    <button
                      className="tw-mr-5 tw-font-semibold tw-text-[#295E7D]"
                      onClick={handleAddRow}
                    >
                      Add Variant
                    </button>
                  )}
                  <button
                    className="tw-font-semibold tw-text-[#295E7D]"
                    onClick={handleDistributePercentEvenly}
                  >
                    % Distribute evenly
                  </button>
                </div>
              </div>
            )}
          </CollapsableForm>
        </DisableContainer>
      </div>
    </div>
  );
};
